/**
 * Read a PDF well enough to shelve it: how many pages, and what the front cover looks
 * like. Both shelves use this — the annual reports and the printed newsletters — and the
 * one thing they disagree about is where on page one the cover is. See `rightHalf`.
 *
 * THIS FILE USED TO DO MUCH MORE, and the history is worth a paragraph because the shape
 * of what is left only makes sense with it. It was src/app/lib/anonymisePdf.js, and it
 * built a second edition of every report for view-only accounts and donors — first by
 * pixelating every photograph in the file, then by finding the faces and drawing a black
 * box over each one. Both are gone, and the covering happens before the file gets here
 * now: somebody blurs or blacks out the faces in the PDF, and says so when they shelve
 * it. One file, fit for everybody, stored once and served to everybody.
 *
 * Which is why there is nothing in this file about anonymising anything, and why the
 * page above it spends more words on a tick-box than on any of this. Reports shelved
 * before the change keep the separately prepared edition they came with; the Worker
 * still stores and serves it. See reportEdition in src/worker/index.js.
 *
 * So: no pdf-lib, no image decoding, no detector. pdf.js draws a cover and counts pages,
 * and it is still a dynamic import, because it is 1.6 MB for one Settings page an owner
 * opens a few times a year.
 */

/** pdf.js, loaded only when a report is actually being read. It is 1.6 MB. */
let pdfjsPromise = null
const pdfjs = () => (pdfjsPromise ??= import('unpdf/pdfjs'))

/** How wide a cover is drawn, whichever shelf it is going on. */
const COVER_WIDTH = 900

/**
 * Draw a page, or the right-hand half of it.
 *
 * The half is how these reports are imposed, and it is also why the cover cannot simply
 * be the first image in the file: the cover of a printed report is a composition of
 * pictures and type, and only rendering it produces the thing a reader recognises.
 */
async function renderPage(data, pageNumber, { width, rightHalf = false } = {}) {
  const lib = await pdfjs()
  const doc = await lib.getDocument({ data, isEvalSupported: false, useSystemFonts: true }).promise
  try {
    const page = await doc.getPage(pageNumber)
    const base = page.getViewport({ scale: 1 })
    const sourceWidth = rightHalf ? base.width / 2 : base.width
    const scale = width / sourceWidth
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(sourceWidth * scale)
    canvas.height = Math.round(base.height * scale)
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({
      canvasContext: ctx,
      viewport,
      // Slide the page left so the right-hand half is what lands on the canvas.
      transform: rightHalf ? [1, 0, 0, 1, -base.width * scale * 0.5, 0] : undefined,
    }).promise
    return canvas
  } finally {
    await doc.destroy?.()
  }
}

const canvasToBytes = async (canvas, quality) =>
  new Uint8Array(
    await new Promise((resolve) =>
      canvas.toBlob((b) => b.arrayBuffer().then(resolve), 'image/jpeg', quality)
    )
  )

/** How many pages, without rendering any of them. */
export async function pageCount(data) {
  const lib = await pdfjs()
  const doc = await lib.getDocument({ data, isEvalSupported: false }).promise
  try {
    return doc.numPages
  } finally {
    await doc.destroy?.()
  }
}

/**
 * Read one document: its page count and its front cover, ready to shelve.
 *
 * `rightHalf` is the only thing the two shelves disagree about, and it is a fact about
 * how the file was made rather than a preference. The annual reports are imposed for
 * print — two landscape pages side by side, back cover on the left, front cover on the
 * right — so a thumbnail of the whole sheet would be a picture of the back of the book.
 * The newsletters are ordinary portrait pages and page one is the cover, so taking half
 * of it would cut the masthead down the middle.
 *
 * What comes back deliberately has no year and no date on it. Which of those addresses
 * the document is the shelf's business, and the caller attaches it — this function has
 * one job, which is turning a file into a page count, a cover and something uploadable.
 *
 * `pdf.js` is handed a copy of the bytes rather than the array itself, here and in
 * pageCount, because it transfers the buffer it is given to its worker and leaves the
 * original detached — and the original is the file that still has to be uploaded.
 */
export async function readShelfPdf(file, { title, rightHalf = false, onProgress } = {}) {
  const source = new Uint8Array(await file.arrayBuffer())

  onProgress?.({ phase: 'reading' })
  const pages = await pageCount(source.slice())

  onProgress?.({ phase: 'cover' })
  const cover = await canvasToBytes(
    await renderPage(source.slice(), 1, { width: COVER_WIDTH, rightHalf }),
    0.82
  )

  return {
    title,
    source: file.name,
    page_count: pages,
    bytes: source.byteLength,
    files: {
      file: new Blob([source], { type: 'application/pdf' }),
      cover: new Blob([cover], { type: 'image/jpeg' }),
    },
    // Shown on the page so that whoever is shelving can see they picked the right file.
    // It is the printed cover, which is the only cover there is.
    coverUrl: URL.createObjectURL(new Blob([cover], { type: 'image/jpeg' })),
  }
}

/** The year printed in a filename. */
export const yearOf = (filename) => {
  const m = /(19|20)\d{2}/.exec(String(filename ?? ''))
  return m ? Number(m[0]) : null
}

/**
 * The day printed in a filename, as 'YYYY-MM-DD', or null.
 *
 * How a newsletter gets its place on the shelf: these files are named
 * "2014 03 28 S4CA-Newsletter.pdf", and the date in the name is the only record of when
 * the issue went out. Separators are optional and may be spaces, dashes, dots or
 * underscores, because thirty years of files were not named by one person.
 *
 * THE DAY HAS TO BE ON THE CALENDAR, and that is the part worth keeping. One of the
 * files in the folder this shelf was filled from is named "2022 11 31", and November has
 * thirty days. `new Date('2022-11-31')` does not complain — it is quietly the first of
 * December — so a lenient parser here would move a publication date by two days and
 * nobody would ever find out. Returning null instead makes the import say which file it
 * would not take and why, and the fix is to correct the filename, which is where the
 * mistake actually is.
 *
 * Mirrored in the Worker by `calendarDate` in src/worker/util.js, which will not store a
 * date this would not have produced.
 */
export const dateOf = (filename) => {
  const m = /(19|20)\d{2}[-_. ]?(0[1-9]|1[0-2])[-_. ]?(0[1-9]|[12]\d|3[01])/.exec(
    String(filename ?? '')
  )
  if (!m) return null
  const [year, month, day] = [Number(m[0].slice(0, 4)), Number(m[2]), Number(m[3])]
  const dt = new Date(Date.UTC(year, month - 1, day))
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) {
    return null
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Draw a whole report, a page at a time, for a reader who is not being given the file.
 *
 * A donor reads a shelved document as pictures of its pages rather than in the browser's
 * own PDF viewer, because that viewer arrives with a download button, a print button and
 * a URL of its own. Both shelves do it — see ShelfPages in
 * src/app/components/PdfShelf.jsx, and reportIsForReadingOnly in the Worker, which is
 * the other half.
 *
 * Each page becomes a JPEG and its canvas is dropped. That is not a detail: a
 * forty-page report held as canvases at this width is about two hundred megabytes of
 * bitmap, which is enough to end the tab on a phone, and the same pages as JPEGs are
 * about ten. The document is opened once for all of them rather than once per page.
 *
 * `onPage` is called as each one is ready, so the reader gets page one while page forty
 * is still drawing. `signal` aborts a report somebody closed halfway through — without
 * it, shutting the window would leave the rest of the pages rendering into nothing.
 */
export async function renderReportPages(data, { width = 1100, onPage, signal } = {}) {
  const lib = await pdfjs()
  const doc = await lib.getDocument({ data, isEvalSupported: false, useSystemFonts: true }).promise
  try {
    for (let n = 1; n <= doc.numPages; n++) {
      if (signal?.aborted) return
      const page = await doc.getPage(n)
      const base = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: Math.min(width / base.width, 2) })
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport }).promise
      if (signal?.aborted) return
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.86))
      // Freed before the next page is drawn rather than at the end of the loop.
      canvas.width = 0
      canvas.height = 0
      page.cleanup?.()
      onPage?.({ url: URL.createObjectURL(blob), page: n, total: doc.numPages })
    }
  } finally {
    await doc.destroy?.()
  }
}
